import type { AxiosRequestConfig, AxiosResponse } from "axios";

import { apiConfig, buildUrl, paths } from "@/api/url";
import type { AuthConfigParams, BootConfig } from "@/auth/types";
import type { SaveAccessPayload } from "@/features/AdminPortal/types/access";
import { http } from "@/lib/http";
import type { PowerBiEmbedConfig, PowerBiReport } from "@/types/powerbi";

let authConfigPromise: Promise<AxiosResponse<BootConfig>> | null = null;
const postLoginPromises = new Map<string, Promise<unknown>>();

/**
 * Simple single-flight helper to prevent duplicate simultaneous requests
 * Keyed by a string. The promise is removed once settled.
 */
const _singleFlight = new Map<string, Promise<unknown>>();
function singleFlight<T>(key: string, fn: () => Promise<T>): Promise<T> {
  if (_singleFlight.has(key)) return _singleFlight.get(key) as Promise<T>;
  const p = fn().finally(() => _singleFlight.delete(key));
  _singleFlight.set(key, p);
  return p;
}

export type FileInfo = {
  id: string;
  name: string;
  url: string;
  size: number;
  contentType?: string;
};

export type TimeSlot = { startISO: string; endISO: string };

/* ==========================
   Calendar Types
   ========================== */
export type CalendarEvent = {
  quarter: string;
  reviewDate: string;
  prereadDeadline: string;
  platform: string;
  partnership: string;
  pointPerson: string;
  budget: number | null;
  notes: string;
};

export type OutOfOffice = {
  name: string;
  startDate: string;
  endDate: string;
  reason: string;
};

const v1 = {
  /* ==========================
     Files
     ========================== */
  uploadFiles: async (files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    const { data } = await http.post(buildUrl(paths.v1.files()), form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.files as FileInfo[];
  },

  downloadFileUrl: (fileId: string) => buildUrl(paths.v1.fileById(fileId)),

  getFileMetadata: async (fileId: string) => {
    const { data } = await http.get(buildUrl(paths.v1.fileMeta(fileId)));
    return data as FileInfo;
  },

  /* ==========================
     Disbursements
     ========================== */
  listDisbursements: async (
    q: { status?: string; country?: string; page?: number; pageSize?: number } = {}
  ) => {
    const sp = new URLSearchParams();
    if (q.status) sp.set("status", q.status);
    if (q.country) sp.set("country", q.country);
    if (q.page) sp.set("page", String(q.page));
    if (q.pageSize) sp.set("pageSize", String(q.pageSize));

    const { data, headers } = await http.get(
      buildUrl(`${paths.v1.disbursements()}?${sp.toString()}`)
    );

    return {
      items: data as unknown[],
      total: Number(headers["x-total-count"] ?? 0),
    };
  },

  createDisbursement: async (body: Record<string, unknown>, idemKey?: string) => {
    const { data } = await http.post(buildUrl(paths.v1.disbursements()), body, {
      headers: idemKey ? { "Idempotency-Key": idemKey } : undefined,
    });
    return data;
  },

  getDisbursement: async (id: string) => {
    const { data } = await http.get(buildUrl(paths.v1.disbursementById(id)));
    return data;
  },

  updateDisbursement: async (id: string, patch: Record<string, unknown>) => {
    const { data } = await http.patch(buildUrl(paths.v1.disbursementById(id)), patch);
    return data;
  },

  cancelDisbursement: async (id: string) => {
    const { data } = await http.post(buildUrl(paths.v1.disbursementCancel(id)), {});
    return data;
  },

  /* ==========================
     Microsoft / Realtime
     ========================== */
  msGroupMembers: async (groupId?: string) => {
    const gid = groupId ?? apiConfig.defaultGroupId;
    if (!gid) throw new Error("DEFAULT_M365_GROUP_ID not configured");
    const key = `msGroupMembers:${gid}`;
    return singleFlight(key, async () => {
      const { data } = await http.get(buildUrl(paths.v1.msGroupMembers(gid)));
      return data as Array<{
        id: string;
        displayName: string;
        mail?: string;
        userPrincipalName: string;
      }>;
    });
  },

  findMeetingTimes: async (body: Record<string, unknown>) => {
    const { data } = await http.post(buildUrl(paths.v1.findMeetingTimes()), body);
    return data;
  },

  scheduleMeeting: async (body: Record<string, unknown>) => {
    const { data } = await http.post(buildUrl(paths.v1.scheduleMeeting()), body);
    return data;
  },

  getAvailability: async (body: Record<string, unknown>) => {
    const { data } = await http.post(buildUrl(paths.v1.getAvailability()), body);
    return data;
  },

  // msMe: async () => {
  //   return singleFlight("msMe", async () => {
  //     const { data } = await http.get(buildUrl(paths.v1.msMe()));
  //     return data as {
  //       id: string;
  //       displayName: string;
  //       email: string;
  //       role?: string;
  //       organization?: string;
  //     };
  //   });
  // },

  // msMePhotoUrl: () => buildUrl(paths.v1.msMePhoto()),

  negotiateRealtime: async (userId: string) => {
    const key = `negotiate:${userId}`;
    return singleFlight(key, async () => {
      const { data } = await http.get(buildUrl(paths.v1.realtimeNegotiate(userId)));
      return data as { url: string; group: string };
    });
  },

  notifyUser: async (userId: string, body: { message: string; title?: string; data?: unknown }) => {
    const { data } = await http.post(buildUrl(paths.v1.notifyUser(userId)), body);
    return data as { ok: true };
  },

  /* ==========================
     ✅ CALENDAR APIs (NEW)
     ========================== */
  getCalendarEvents: async () => {
    const { data } = await http.get(buildUrl(paths.v1.calendar.events()));
    return data as CalendarEvent[];
  },

  getOutOfOffice: async () => {
    const { data } = await http.get(buildUrl(paths.v1.calendar.ooo()));
    return data as OutOfOffice[];
  },

  /* ==========================
     Auth Config
     ========================== */
  getPartnersTable: async () => {
    const { data } = await http.get(buildUrl(paths.v1.getPartnersTable()));
    return data;
  },

  getAnalytics: async () => {
    const { data } = await http.get(buildUrl(paths.v1.analytics()));
    return data;
  },
  getPartnersDirectory: async (countryId?: string, searchValue?: string) => {
    const { data } = await http.post(buildUrl(paths.v1.getPartnersDirectory()), {
      countryId,
      searchValue,
    });
    return data;
  },
  getCountries: async () => {
    const { data } = await http.get(buildUrl(paths.v1.getPartnersDirectory() + "/countries"));
    return data;
  },
  getPrimaryContact: async (userId: string) => {
    const { data } = await http.post(buildUrl(paths.v1.getPrimaryContact()), { userId });
    return data;
  },

  AuthConfig: async (
    params?: AuthConfigParams,
    axiosConfig?: Omit<AxiosRequestConfig, "url" | "method" | "params">
  ): Promise<BootConfig> => {
    const url = buildUrl(paths.v1.authConfig());

    // if (!authConfigPromise) {
    authConfigPromise = http.get<BootConfig>(url, {
      ...(axiosConfig ?? {}),
      headers: {
        Accept: "application/json",
        ...(axiosConfig?.headers ?? {}),
      },
      params,
    });
    // }

    const response = await authConfigPromise;
    return response.data;
  },

  postLogin: async (userPrincipalName: string) => {
    if (!postLoginPromises.has(userPrincipalName)) {
      postLoginPromises.set(
        userPrincipalName,
        http.post(buildUrl(paths.v1.authConfigByUser(userPrincipalName)))
      );
    }

    const result = await postLoginPromises.get(userPrincipalName)!;
    // If result is an AxiosResponse, return .data, else return as is
    if (typeof result === "object" && result !== null && "data" in result) {
      return (result as { data: unknown }).data;
    }
    return result;
  },

  // todaysPriorities: async (roleId: string, userId: string) => {
  //   const { data } = await http.get(buildUrl(paths.v1.todaysPriorities(roleId, userId)));
  //   return data;
  // },

  partnerHighlights: async () => {
    const { data } = await http.get(buildUrl(paths.v1.partnerHighlights()));
    return data;
  },

  impactTargets: async () => {
    const { data } = await http.get(buildUrl(paths.v1.impactTargets()));
    return data;
  },

  resources: async () => {
    const { data } = await http.get(buildUrl(paths.v1.resources()));
    return data;
  },

  ideaStatsPeriods: async () => {
    const { data } = await http.get(buildUrl(paths.v1.ideaStatsPeriods()));
    return data;
  },

  ideaStatsQuarterly: async (quarter: string) => {
    const { data } = await http.get(buildUrl(paths.v1.ideaStatsQuarterly(quarter)));
    return data;
  },

  paymentsPipeline: async () => {
    const { data } = await http.get(buildUrl(paths.v1.paymentsPipeline()));
    return data;
  },
  quickLinks: async (upn: string, role: string) => {
    const { data } = await http.get(buildUrl(paths.v1.quickLinks(upn, role)));
    return data;
  },

  addQuickLink: async (payload: Record<string, unknown>) => {
    const { data } = await http.post(buildUrl(paths.v1.addQuickLink()), payload);
    return data;
  },
  reorderQuickLinks: async (
    payload: { id: string; displayOrder: number }[],
    upn: string,
    role: string
  ) => {
    const { data } = await http.put(buildUrl(paths.v1.reorderQuickLinks()), {
      upn,
      roleCode: role,
      updates: payload,
    });
    return data;
  },
  deleteQuickLink: async (id: string) => {
    const { data } = await http.delete(buildUrl(paths.v1.deleteQuickLink(id)));
    return data;
  },

    quickLinksConfig: async () => {
      const { data } = await http.get(buildUrl(paths.v1.quickLinksConfig()));
      return data;
    },
   
quickLinksConfigUpdate: async (payload: {
  roleCode: string;
  appName: string;
  appUrl: string;
}) => {
  const { data } = await http.put(
    buildUrl(paths.v1.quickLinksConfigUpdate()),
    payload
  );
  return data;
},


  /* ==========================
      Timezones
      ========================== */
  timezones: async (upn: string) => {
    const { data } = await http.get(buildUrl(paths.v1.timezones(upn)));
    return data;
  },

  saveTimezones: async (payload: any) => {
    const { data } = await http.post(buildUrl(paths.v1.saveTimezones()), payload);
    return data;
  },
  /* ==========================
     Language Preference
      ========================== */
  language: async (upn: string) => {
    const { data } = await http.get(buildUrl(paths.v1.language(upn)));
    return data;
  },

  saveLanguage: async (payload: any) => {
    const { data } = await http.post(buildUrl(paths.v1.saveLanguage()), payload);
    return data;
  },

  timezoneConfig: async () => {
  const { data } = await http.get(buildUrl(paths.v1.timezoneConfig()));
  return data;
},

timezoneConfigUpdate: async (payload: {
  items: {
    Id: number;
    OfficeStartHour: number;
    OfficeEndHour: number;
  }[];
}) => {
  const { data } = await http.put(
    buildUrl(paths.v1.timezoneConfigUpdate()),
    payload
  );
  return data;
},
    /* ==========================
     Dashboard
      ========================== */
      dashboardConfig: async () => {
  const { data } = await http.get(buildUrl(paths.v1.dashboardConfig()));
  return data;
    },

    dashboardConfigUpdate: async (payload: {
      roleCode: string;
      widgets: { feature_code: string; display_order: number }[];
      updatedBy: string;
    }) => {
      const { data } = await http.put(
        buildUrl(paths.v1.dashboardConfigUpdate()),
        payload
      );
      return data;
    },
    /* ==========================
        Common Configurations
          ========================== */
    commonConfig: async () => {
      const { data } = await http.get(buildUrl(paths.v1.commonConfig()));
      return data;
    },

    commonConfigUpdate: async (payload: {
      LogoutTime: number;
      WarningTime: number;
      InactiveDays: number;
    }) => {
      const { data } = await http.put(
        buildUrl(paths.v1.commonConfigUpdate()),
        payload
      );
      return data;
    },
  /* ==========================
     Admin Portal - Access Management
     ========================== */
  getAccessMatrix: async () => {
    const data = await http.get(buildUrl(paths.v1.accessMatrix()));
    return data;
  },
  saveAccessChanges: async (payload: SaveAccessPayload) => {
    const { data } = await http.post(buildUrl(paths.v1.saveAccessChanges()), payload);
    return data;
  },
  getUserRoles: async (upn: string) => {
    return await http.get(buildUrl(paths.v1.getUserRoles(upn)));
  },
  saveUserRoles: async (payload: {
    user_principal_name: string;
    roles: { role_id: number; approval_type: "reviewer" | "approver" }[];
  }) => {
    const { data } = await http.post(buildUrl(paths.v1.saveUserRoles()), payload);
    return data;
  },
  getUserFeatures: async (email: string) => {
    return await http.get(buildUrl(paths.v1.getUserFeatures(email)));
  },
  getUserDetails: async (email: string) => {
    return await http.get(buildUrl(paths.v1.getUserDetails(email)));
  },

  /**
   * ====================================================================
   * NOTIFICATIONS API
   * ====================================================================
   */

  /**
   * Fetch unread count from the database (source of truth)
   * Called on app load, refresh, and reconnect
   */
  getUnreadCount: async () => {
    return singleFlight("notifications:unreadCount", async () => {
      const { data } = await http.get(buildUrl(paths.v1.notificationsUnreadCount()));
      return data.data;
    });
  },

  /**
   * Fetch all notifications
   */
  // getNotifications: async (
  //   query: {
  //     limit?: number;
  //     offset?: number;
  //     isRead?: boolean;
  //   } = {}
  // ) => {
  //   const params = new URLSearchParams();
  //   if (query.limit) params.append("limit", String(query.limit));
  //   if (query.offset) params.append("offset", String(query.offset));
  //   if (query.isRead !== undefined) params.append("isRead", String(query.isRead));

  //   const { data } = await http.get(
  //     buildUrl(paths.v1.notifications()) + (params.toString() ? `?${params}` : "")
  //   );
  //   return data.data;
  // },

  /**
   * Mark notifications in a chat group as read
   * Called when user opens a chat
   */
  markChatGroupAsRead: async (groupId: string) => {
    const { data } = await http.post(buildUrl(paths.v1.markChatGroupRead(groupId)));
    return data.data;
  },

  /* ==========================
     User Tour
     ========================== */

  getUserTourDecision: async (userPrincipalName: string) => {
    const { data } = await http.get(buildUrl(paths.v1.userTourDecision(userPrincipalName)));
    return data;
  },

  postUserTourShown: async (userPrincipalName: string) => {
    const { data } = await http.post(buildUrl(paths.v1.userTourShown(userPrincipalName)));
    return data;
  },

  /**
   * Get available Power BI reports for a workspace (group)
   * Backend returns metadata only - never handles tokens or credentials
   * @param groupId - Power BI workspace group ID
   * @returns Array of available reports
   */
  getPowerBiReports: async (groupId: string): Promise<PowerBiReport[]> => {
    const { data } = await http.get(buildUrl(paths.v1.powerbiReports(groupId)));
    return data.value as PowerBiReport[];
  },

  /**
   * Get Power BI embed configuration for a specific report
   * Backend issues short-lived tokens - handles all security
   * @param groupId - Power BI workspace group ID
   * @param reportId - Power BI report ID
   * @returns Embed configuration with token and URL
   */
  getPowerBiEmbedConfig: async (groupId: string, reportId: string): Promise<PowerBiEmbedConfig> => {
    const { data } = await http.get<PowerBiEmbedConfig>(
      buildUrl(paths.v1.powerbiEmbedConfig(groupId, reportId))
    );
    return data;
  },
  getFluxxUserId: async (userId: string) => {
    const { data } = await http.post(buildUrl(paths.v1.getFluxxUserId()), { userId });
    return data;
  },
};

export const api = v1;
export type Api = typeof api;
